import classnames from 'classnames';
import { forwardRef } from 'react';

import { Link } from '@/core/routing/components/Link';
import { Li } from '@/core/tamagoshiv2/components/Typography/Typography';
import { ArrowLeftIcon } from '@/core/tamagoshiv2/icons/controls/ArrowLeftIcon';
import { type TamagoshiComponent } from '@/core/tamagoshiv2/interfaces';

import styles from './Breadcrumbs.module.scss';

type BreadcrumbsProps = TamagoshiComponent & {
  levels: { content: string; href: string }[];
};

export const Breadcrumbs = forwardRef<HTMLElement, BreadcrumbsProps>(
  ({ className, levels, ...props }, ref) => {
    if (levels.length === 0) return null;

    const crumbs = [...levels];
    const { content: lastCrumb } = crumbs.pop()!;

    return (
      <nav className={classnames(styles.root, className)} ref={ref} {...props}>
        <ArrowLeftIcon className={styles.back} />
        <ol aria-label="Breadcrumbs" className={styles.crumbs}>
          {crumbs.map(({ content, href }, index) => (
            <Li
              className={classnames(
                styles.crumb,
                index > 0 && styles.shrinkable,
                index === crumbs.length - 1 && styles.previous,
              )}
              key={href}
              variant="small"
            >
              {index > 0 && (
                <span aria-hidden="true" className={styles.separator}>
                  /
                </span>
              )}
              <Link
                className={classnames(styles.crumbLink, styles.crumbText)}
                href={href}
                title={content}
              >
                {content}
              </Link>
            </Li>
          ))}
          <Li
            aria-current="page"
            className={classnames(styles.crumb, styles.last)}
            variant="small"
          >
            <span aria-hidden="true" className={styles.separator}>
              /
            </span>
            <span className={styles.crumbText} title={lastCrumb}>
              {lastCrumb}
            </span>
          </Li>
        </ol>
      </nav>
    );
  },
);

Breadcrumbs.displayName = 'Breadcrumbs';
